import { DomainError, type Lesson } from "./lesson-schema";
import type { Cursor, ReentryPlan, Session, SessionAction, SessionEvent } from "./session-schema";

export type SessionOptions = {
  readonly id?: string;
  readonly mode?: "hybrid" | "all-live";
  readonly providerPath?: string;
};

export function createSession(lesson: Lesson, options: SessionOptions = {}): Session {
  const first = lesson.phrases[0];
  if (!first) throw new DomainError("invalid_state", "A session requires at least one phrase.");
  const id = options.id ?? crypto.randomUUID();
  return {
    schemaVersion: 1,
    id,
    lesson,
    mode: options.mode ?? "hybrid",
    status: "created",
    generationEpoch: 0,
    bridgeFence: 0,
    turnId: `${id}:0`,
    providerPath: options.providerPath ?? "local-preview",
    speechOwner: "none",
    committedCursor: { phraseId: first.id, phraseIndex: 0, textOffset: first.startOffset },
    interruptionAnchor: null,
    candidate: null,
    question: null,
    answer: null,
    reentryPlan: null,
    lastBridgeId: null,
    recoveryReason: null,
    resumeStatus: null,
    processedEventIds: [],
    confirmedOnsetIds: [],
    revision: 0,
  };
}

export function eventFor(
  session: Session,
  action: SessionAction,
  eventId: string = crypto.randomUUID(),
): SessionEvent {
  return {
    ...action,
    eventId,
    sessionId: session.id,
    turnId: session.turnId,
    generationEpoch: session.generationEpoch,
    bridgeFence: session.bridgeFence,
    providerPath: session.providerPath,
    clauseId: session.lesson.phrases[session.committedCursor.phraseIndex]?.clauseId ?? null,
    at: new Date().toISOString(),
  };
}

export function reentryPlan(session: Session, anchor: Cursor): ReentryPlan {
  const phrase = session.lesson.phrases[anchor.phraseIndex];
  const start = phrase?.startOffset ?? anchor.textOffset;
  const restoration = start < anchor.textOffset;
  return {
    class: restoration ? "phrase-restoration" : "direct",
    phraseId: anchor.phraseId,
    replayStartOffset: start,
    firstUnplayedOffset: anchor.textOffset,
    owner: session.mode === "hybrid" ? "course-pack" : "live",
    bridgeText: null,
    bridgeId: null,
    policyVersion: null,
    reason: restoration ? "phrase-restoration" : "direct-continuation",
    leadInDurationMs: null,
    visualHandle: null,
    requiresAlignment: true,
  };
}

export function narrationOwner(session: Session): Session["speechOwner"] {
  return session.providerPath === "local-preview"
    ? "text-preview"
    : session.mode === "hybrid"
      ? "course-pack"
      : "live-avatar";
}
